import { api } from '@/lib/api';
import type { SourceCoverage } from '@waypoint/shared';

export interface DealRiskItem {
  id: string;
  dealName: string;
  company: string;
  name: string;
  /** Canonical clients.id for drill-down. Null when the deal's contact isn't
   *  linked to a client record yet; callers fall back to a name slug. */
  clientId: string | null;
  value: number;
  stage: string;
  riskScore: number;
  survivalProbability: number;
  daysStalled: number;
  daysSinceActivity: number;
  /** Cited evidence, most important first. */
  riskFactors: string[];
  /** The sales engine's one-line reason for this deal, when it has one. */
  headline: string | null;
  action: { kind: string; label: string } | null;
  /** The prospect said no in writing — lost until the CRM agrees. */
  likelyLost: boolean;
  nextMeetingAt: string | null;
}

export interface RecentWin {
  id: string;
  title: string;
  company: string | null;
  amount_cents: number | null;
  closed_at: string;
  /** CRM provider the win came from, or 'logged' for a win noted by hand. */
  source: string;
  clientId: string | null;
}

export interface RecentWinsResult {
  wins: RecentWin[];
  days: number;
  totalCents: number;
  coverage: SourceCoverage;
}

export async function getRecentWins(days = 30): Promise<RecentWinsResult> {
  return api(`/v1/deals/wins?days=${days}`);
}

export interface ScopeBadge {
  level: 'org' | 'team' | 'self' | 'none';
  label: string;
  isOrgWide: boolean;
  departmentIds: string[];
  departmentNames: string[];
  attributionEmailCount: number;
}

export interface DealRiskResult {
  coverage: SourceCoverage;
  /** null (here and in summary) when no CRM is connected and synced: not an empty book, an unseen one. */
  deals: DealRiskItem[] | null;
  summary: {
    total: number;
    atRisk: number;
    /** Deals whose contact said no in writing. Not counted in atRisk. */
    likelyLost?: number;
    healthy: number;
    /** Pipeline KPI tiles — computed server-side from the open-deal set. */
    stalledCount?: number;
    avgDealValueCents?: number;
    velocityScore?: number;
    newThisWeek?: number;
  } | null;
  scope?: ScopeBadge;
}

export async function getDealRisk(): Promise<DealRiskResult> {
  return api('/v1/deals/risk');
}
